import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/ui/Layout';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { useAuth } from '../../components/context/AuthContext';
import { useNotification } from '../../components/ui/Notification';
import { listUnprocessedPayments } from '../../services/payment/listUnprocessedPayments';
import { searchAdminPayments } from '../../services/payment/searchAdminPayments';
import {
  finalizeCartPaymentAsFailed,
  finalizeCartPaymentAsSuccess,
} from '../../services/payment/finalizeCartPayment';
import { verifyWipayPayment } from '../../services/payment/verifyWipayPayment';
import { createLogger } from '../../utils/logger';
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';

const logger = createLogger('AdminUnprocessedPaymentsPage');

function formatDate(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatCurrency(amount, currency = 'JMD') {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '—';
  return `${currency} $${numeric.toFixed(2)}`;
}

function getWipayStatusVariant(status) {
  if (!status) return 'secondary';
  const normalized = String(status).toLowerCase();
  if (normalized === 'success') return 'success';
  if (normalized === 'failed' || normalized === 'failure') return 'danger';
  if (normalized === 'no_callback') return 'warning';
  return 'info';
}


function getVerifySummaryVariant(overall) {
  if (overall === 'verified') return 'success';
  if (overall === 'hash_invalid' || overall === 'wapi_mismatch') return 'danger';
  if (overall === 'firestore_incomplete' || overall === 'missing_callback') return 'warning';
  return 'info';
}

function VerifyResultModal({ result, show, onHide }) {
  if (!result) return null;

  const { summary, hashVerification, wapiLookup, firestoreComparison, wipay } = result;

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>WiPay verification — {result.orderId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {summary && (
          <Alert variant={getVerifySummaryVariant(summary.overall)}>
            <strong>{summary.label}</strong>
            {summary.recommendation && (
              <div className="mt-1 mb-0">{summary.recommendation}</div>
            )}
          </Alert>
        )}

        {wipay && (
          <>
            <h6 className="mt-3">Stored WiPay callback</h6>
            <ul className="small mb-0">
              <li>
                Status: <strong>{wipay.status || '—'}</strong>
              </li>
              <li>
                Transaction ID:{' '}
                <span className="font-monospace">{wipay.transactionId || '—'}</span>
              </li>
              <li>
                Total: {wipay.total != null ? `${wipay.currency || 'JMD'} $${Number(wipay.total).toFixed(2)}` : '—'}
              </li>
              {wipay.message && <li>Message: {wipay.message}</li>}
            </ul>
          </>
        )}

        {hashVerification && (
          <>
            <h6 className="mt-3">Response hash verification</h6>
            {hashVerification.checked ? (
              <ul className="small mb-0">
                <li>
                  Result:{' '}
                  <Badge bg={hashVerification.valid ? 'success' : 'danger'}>
                    {hashVerification.valid ? 'Valid' : 'Invalid'}
                  </Badge>
                </li>
                {hashVerification.matchedOriginalTotal && (
                  <li>Matched request total: {hashVerification.matchedOriginalTotal}</li>
                )}
                {hashVerification.matchedSource && (
                  <li>Matched source: {hashVerification.matchedSource}</li>
                )}
                {hashVerification.reason && <li>{hashVerification.reason}</li>}
              </ul>
            ) : (
              <p className="small text-muted mb-0">{hashVerification.reason}</p>
            )}
          </>
        )}

        {wapiLookup && (
          <>
            <h6 className="mt-3">WiPay WAPI lookup</h6>
            {wapiLookup.checked ? (
              <ul className="small mb-0">
                <li>
                  Result:{' '}
                  {wapiLookup.ok ? (
                    <Badge bg="success">Found</Badge>
                  ) : (
                    <Badge bg="secondary">Not found / failed</Badge>
                  )}
                </li>
                {wapiLookup.status && <li>Remote status: {wapiLookup.status}</li>}
                {wapiLookup.reason && <li>{wapiLookup.reason}</li>}
              </ul>
            ) : (
              <p className="small text-muted mb-0">{wapiLookup.reason}</p>
            )}
          </>
        )}

        {firestoreComparison && (
          <>
            <h6 className="mt-3">Firestore alignment</h6>
            <ul className="small mb-0">
              <li>WiPay record: {firestoreComparison.hasWipayRecord ? 'Yes' : 'No'}</li>
              <li>Transaction record: {firestoreComparison.hasTransactionRecord ? 'Yes' : 'No'}</li>
              <li>WiPay status: {firestoreComparison.wipayStatus || '—'}</li>
              <li>Transaction status: {firestoreComparison.transactionStatus || '—'}</li>
              <li>Cart status: {firestoreComparison.cartStatus || '—'}</li>
              {firestoreComparison.mismatches?.length > 0 && (
                <li className="text-warning">
                  Mismatches: {firestoreComparison.mismatches.join(' ')}
                </li>
              )}
            </ul>
          </>
        )}

        {result.documentation && (
          <p className="small text-muted mt-3 mb-0">
            Hash verification follows the WiPay Payments API transaction response docs.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function PaymentTable({ payments, actionLoadingId, verifyLoadingId, onResolve, onVerify }) {
  return (
    <div className="table-responsive">
      <Table striped bordered hover size="sm" className="align-middle">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Cart ID</th>
            <th>Customer</th>
            <th>Email</th>
            <th>WiPay Status</th>
            <th>Persistence issue</th>
            <th>Supermarket</th>
            <th>Amount</th>
            <th>WiPay Transaction</th>
            <th>Method</th>
            <th>Received</th>
            <th>Cart / Transaction</th>
            <th>Message</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => {
            const isResolving = actionLoadingId?.endsWith(`_${payment.orderId}`);
            const isVerifying = verifyLoadingId === payment.orderId;
            const needsResolution = Boolean(payment.issueType);
            return (
              <tr key={payment.id}>
                <td className="font-monospace text-break">{payment.orderId}</td>
                <td className="font-monospace text-break">{payment.cartInstanceId || '—'}</td>
                <td>{payment.customerName || 'Unknown Customer'}</td>
                <td className="text-break">{payment.customerEmail || '—'}</td>
                <td>
                  <Badge bg={getWipayStatusVariant(payment.wipayStatus)}>
                    {payment.wipayStatus || 'unknown'}
                  </Badge>
                  {payment.wipaySucceeded && (
                    <Badge bg="success" className="ms-1">
                      WiPay OK
                    </Badge>
                  )}
                </td>
                <td className="text-break" style={{ maxWidth: 260 }}>
                  <Badge
                    bg={
                      payment.issueType
                        ? payment.wipaySucceeded
                          ? 'warning'
                          : 'secondary'
                        : 'success'
                    }
                    text={payment.issueType && payment.wipaySucceeded ? 'dark' : undefined}
                  >
                    {payment.issueLabel || '—'}
                  </Badge>
                </td>
                <td className="text-break">{payment.supermarketName}</td>
                <td>{formatCurrency(payment.total, payment.currency)}</td>
                <td className="font-monospace text-break">
                  {payment.wipayTransactionId || '—'}
                </td>
                <td>{payment.paymentMethod || '—'}</td>
                <td>{formatDate(payment.receivedAt)}</td>
                <td>
                  <div>
                    <small className="text-muted">Cart:</small> {payment.cartStatus || '—'}
                  </div>
                  <div>
                    <small className="text-muted">Transaction:</small>{' '}
                    {payment.hasTransaction ? payment.transactionStatus : 'None'}
                  </div>
                  {payment.source === 'stuck_cart' && (
                    <Badge bg="warning" text="dark" className="mt-1">
                      No callback
                    </Badge>
                  )}
                </td>
                <td className="text-break" style={{ maxWidth: 220 }}>
                  {payment.message || '—'}
                </td>
                <td>
                  <div className="d-flex flex-column gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={Boolean(actionLoadingId) || Boolean(verifyLoadingId)}
                      onClick={() => onVerify(payment.orderId)}
                    >
                      {isVerifying ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                    {needsResolution ? (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          disabled={Boolean(actionLoadingId) || Boolean(verifyLoadingId)}
                          onClick={() => onResolve(payment.orderId, 'success')}
                        >
                          {isResolving && actionLoadingId?.startsWith('success_') ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            'Mark Successful'
                          )}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={Boolean(actionLoadingId) || Boolean(verifyLoadingId)}
                          onClick={() => onResolve(payment.orderId, 'failed')}
                        >
                          {isResolving && actionLoadingId?.startsWith('failed_') ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            'Mark Failed'
                          )}
                        </Button>
                      </>
                    ) : (
                      <span className="text-muted small">No resolve action needed</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

export default function AdminUnprocessedPaymentsPage() {
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [verifyLoadingId, setVerifyLoadingId] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [activeSearchLabel, setActiveSearchLabel] = useState('');

  const breadcrumbItems = [
    { path: '/', label: 'Home' },
    { path: '/admin', label: 'Admin Panel' },
    { label: 'Unprocessed Payments' },
  ];

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await listUnprocessedPayments();
      setPayments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      logger.error('[AdminUnprocessedPaymentsPage] Failed to load payments:', err);
      setError('Failed to load unprocessed payments. Please try again.');
      showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load unprocessed payments.',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const term = searchQuery.trim();
    if (!term) {
      showNotification({
        type: 'error',
        title: 'Search required',
        message: 'Enter a cart ID, order ID, transaction ID, email, or customer name.',
        duration: 4000,
      });
      return;
    }

    setSearching(true);
    setError('');
    setIsSearchMode(true);
    setActiveSearchLabel(term);

    try {
      const rows = await searchAdminPayments(term);
      setPayments(Array.isArray(rows) ? rows : []);
      if (!rows.length) {
        showNotification({
          type: 'info',
          title: 'No matches',
          message: `No payments found for "${term}".`,
          duration: 4000,
        });
      }
    } catch (err) {
      logger.error('[AdminUnprocessedPaymentsPage] Search failed:', err);
      setError('Failed to search payments. Please try again.');
      showNotification({
        type: 'error',
        title: 'Search failed',
        message: 'Could not search payments. Please try again.',
        duration: 5000,
      });
    } finally {
      setSearching(false);
    }
  };

  const handleClearSearch = async () => {
    setSearchQuery('');
    setIsSearchMode(false);
    setActiveSearchLabel('');
    await loadPayments();
  };

  const reloadCurrentView = useCallback(async () => {
    if (isSearchMode && activeSearchLabel) {
      setSearching(true);
      try {
        const rows = await searchAdminPayments(activeSearchLabel);
        setPayments(Array.isArray(rows) ? rows : []);
      } catch (err) {
        logger.error('[AdminUnprocessedPaymentsPage] Failed to refresh search results:', err);
      } finally {
        setSearching(false);
      }
      return;
    }
    await loadPayments();
  }, [activeSearchLabel, isSearchMode, loadPayments]);

  const handleVerify = async (orderId) => {
    setVerifyLoadingId(orderId);
    try {
      const result = await verifyWipayPayment(orderId);
      setVerifyResult(result);
      setShowVerifyModal(true);
    } catch (err) {
      logger.error(`[AdminUnprocessedPaymentsPage] Verify failed for ${orderId}:`, err);
      showNotification({
        type: 'error',
        title: 'Verification failed',
        message: err.message || 'Could not verify payment with WiPay. Please try again.',
        duration: 6000,
      });
    } finally {
      setVerifyLoadingId(null);
    }
  };

  const handleResolve = async (orderId, action) => {
    const confirmMessage =
      action === 'success'
        ? `Mark order ${orderId} as successful? This will create the transaction, update the cart, and decrement inventory.`
        : `Mark order ${orderId} as failed? This will record a failed payment and reset the customer's cart.`;

    if (!window.confirm(confirmMessage)) return;

    setActionLoadingId(`${action}_${orderId}`);
    try {
      const options = { resolvedBy: currentUser?.uid || null };
      if (action === 'success') {
        await finalizeCartPaymentAsSuccess(orderId, options);
        showNotification({
          type: 'success',
          title: 'Payment resolved',
          message: `Order ${orderId} was persisted as successful.`,
          duration: 4000,
        });
      } else {
        await finalizeCartPaymentAsFailed(orderId, options);
        showNotification({
          type: 'success',
          title: 'Payment resolved',
          message: `Order ${orderId} was persisted as failed.`,
          duration: 4000,
        });
      }
      await reloadCurrentView();
    } catch (err) {
      logger.error(`[AdminUnprocessedPaymentsPage] Failed to resolve ${orderId} as ${action}:`, err);
      showNotification({
        type: 'error',
        title: 'Resolution failed',
        message: err.message || 'Could not resolve payment. Please try again.',
        duration: 6000,
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const isBusy = loading || searching;

  return (
    <Layout>
      <Container className="mt-4">
        <BreadcrumbNavigation items={breadcrumbItems} />
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h1 className="mb-1">Unprocessed Payments</h1>
            <p className="text-muted mb-0">
              Review WiPay callbacks and checkout sessions that have not been fully persisted to
              Firestore, including payments that succeeded on WiPay but failed to create or complete
              the transaction.
            </p>
          </div>
          <Button
            variant="outline-primary"
            onClick={reloadCurrentView}
            disabled={isBusy}
          >
            Refresh
          </Button>
        </div>

        <Form onSubmit={handleSearch} className="mb-3">
          <Row className="g-2 align-items-end">
            <Col md={8} lg={9}>
              <Form.Label htmlFor="payment-search">Search payments</Form.Label>
              <Form.Control
                id="payment-search"
                type="search"
                placeholder="Cart ID, order ID, WiPay transaction ID, email, customer name, user ID..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                disabled={isBusy}
              />
              <Form.Text className="text-muted">
                Matches cart instance ID, WiPay order ID, Firestore transaction ID, WiPay transaction
                ID, customer email, name, and user ID.
              </Form.Text>
            </Col>
            <Col md={4} lg={3} className="d-flex gap-2">
              <Button type="submit" variant="primary" disabled={isBusy} className="flex-grow-1">
                {searching ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Searching…
                  </>
                ) : (
                  'Search'
                )}
              </Button>
              {isSearchMode && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleClearSearch}
                  disabled={isBusy}
                >
                  Clear
                </Button>
              )}
            </Col>
          </Row>
        </Form>

        {isSearchMode && activeSearchLabel && (
          <Alert variant="info" className="mb-3">
            Showing search results for <strong>{activeSearchLabel}</strong>.
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {isBusy ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
            <p className="mt-2 text-muted">
              {searching ? 'Searching payments…' : 'Loading pending payments…'}
            </p>
          </div>
        ) : payments.length === 0 ? (
          <Alert variant={isSearchMode ? 'warning' : 'success'}>
            {isSearchMode
              ? `No payments matched "${activeSearchLabel}".`
              : 'No unprocessed payments found.'}
          </Alert>
        ) : (
          <PaymentTable
            payments={payments}
            actionLoadingId={actionLoadingId}
            verifyLoadingId={verifyLoadingId}
            onResolve={handleResolve}
            onVerify={handleVerify}
          />
        )}

        <VerifyResultModal
          result={verifyResult}
          show={showVerifyModal}
          onHide={() => setShowVerifyModal(false)}
        />
      </Container>
    </Layout>
  );
}
